import { differenceInDays, isToday, isYesterday } from 'date-fns';

import { dateHelpers } from '@nocowanie/core';

type TranslationFunction = (key: string, options?: { count?: number }) => string;

interface DateFormatRule {
    condition: (daysDiff: number, reviewDate: Date) => boolean;
    formatter: (daysDiff: number, t: TranslationFunction) => string;
}

// Configuration array defining the formatting rules
const DATE_FORMAT_RULES: DateFormatRule[] = [
    {
        condition: (_, reviewDate) => isToday(reviewDate),
        formatter: (_, t) => t('dateFormatting.today'),
    },
    {
        condition: (_, reviewDate) => isYesterday(reviewDate),
        formatter: (_, t) => t('dateFormatting.yesterday'),
    },
    {
        condition: daysDiff => daysDiff >= 2 && daysDiff <= 6,
        formatter: (daysDiff, t) => t('dateFormatting.daysAgo', { count: daysDiff }),
    },
    {
        condition: daysDiff => daysDiff >= 7 && daysDiff <= 10,
        formatter: (_, t) => t('dateFormatting.weekAgo'),
    },
    {
        condition: daysDiff => daysDiff >= 11 && daysDiff <= 17,
        formatter: (_, t) => t('dateFormatting.weeksAgo', { count: 2 }),
    },
    {
        condition: daysDiff => daysDiff >= 18 && daysDiff <= 24,
        formatter: (_, t) => t('dateFormatting.weeksAgo', { count: 3 }),
    },
    {
        condition: daysDiff => daysDiff >= 25 && daysDiff <= 31,
        formatter: (_, t) => t('dateFormatting.monthAgo'),
    },
];

/**
 * Formats review date based on how long ago it was
 * @param dateString - Date string in format YYYY-MM-DD
 * @param t - Translation function from useTranslation hook
 * @returns Formatted date string using translations
 */
export const formatReviewDate = (dateString: string, t: TranslationFunction): string => {
    const reviewDate = new Date(dateString);
    const today = new Date();
    const daysDifference = Math.abs(differenceInDays(today, reviewDate));

    if (daysDifference <= 31) {
        const matchingRule = DATE_FORMAT_RULES.find(rule =>
            rule.condition(daysDifference, reviewDate),
        );

        if (matchingRule) {
            return matchingRule.formatter(daysDifference, t);
        }
    }

    return dateHelpers.format(reviewDate, 'LLLL yyyy');
};
